import "@/i18n";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ShieldCheck, RotateCcw, Headset } from "lucide-react";
import logoAsset from "@/assets/logo.png.asset.json";
const logo = logoAsset.url;

const STAGE_THRESHOLDS = [0.0, 0.35, 0.7, 0.95];

/**
 * Full-screen "Finding the best rates…" loader shown while we compute the
 * route and prepare vehicle quotes.
 */
export function SearchingOverlay() {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(2);
  const [stageIdx, setStageIdx] = useState(0);

  useEffect(() => {
    const start = performance.now();
    const duration = 5000;
    let raf = 0;
    const tick = (now: number) => {
      const tt = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - tt, 2);
      const pct = Math.round(2 + eased * 96);
      setProgress(pct);

      let idx = 0;
      for (let i = 0; i < STAGE_THRESHOLDS.length; i++) {
        if (tt >= STAGE_THRESHOLDS[i]) idx = i;
      }
      setStageIdx(idx);

      if (tt < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const stages = [
    { title: t("searching.stage1Title"), subtitle: t("searching.stage1Sub") },
    { title: t("searching.stage2Title"), subtitle: t("searching.stage2Sub") },
    { title: t("searching.stage3Title"), subtitle: t("searching.stage3Sub") },
    { title: t("searching.stage4Title"), subtitle: t("searching.stage4Sub") },
  ];

  const features = [
    { icon: ShieldCheck, label: t("searching.securePayment") },
    { icon: RotateCcw, label: t("searching.freeCancellation") },
    { icon: Headset, label: t("searching.available247") },
  ];

  const current = stages[stageIdx];

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-y-auto bg-white bg-gradient-to-b from-white via-primary/5 to-primary/10 px-6 py-10"
    >
      <img src={logo} alt="Bacha Way Travel" className="h-16 w-auto mb-10 animate-pulse" />

      <h1
        key={`t-${stageIdx}`}
        className="text-3xl sm:text-4xl font-serif font-semibold text-foreground text-center max-w-md leading-tight animate-in fade-in slide-in-from-bottom-2 duration-500"
      >
        {current.title}
        <span className="text-primary">…</span>
      </h1>
      <p
        key={`s-${stageIdx}`}
        className="mt-3 text-base text-muted-foreground text-center animate-in fade-in duration-500"
      >
        {current.subtitle}
      </p>

      <div className="mt-8 w-full max-w-sm">
        <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
          <div
            className="h-full bg-foreground rounded-full transition-[width] duration-200 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="mt-3 text-center text-sm text-muted-foreground tabular-nums">
          {progress}% <span className="mx-1 text-muted-foreground/50">•</span>{" "}
          {t("searching.stepOf", { current: stageIdx + 1, total: stages.length })}
        </p>
      </div>

      <ul className="mt-8 w-full max-w-sm space-y-2">
        {stages.map((s, i) => {
          const done = i < stageIdx;
          const active = i === stageIdx;
          return (
            <li
              key={i}
              className={`flex items-center gap-3 text-sm transition-opacity ${
                done || active ? "opacity-100" : "opacity-40"
              }`}
            >
              <span
                className={`inline-flex h-6 w-6 items-center justify-center rounded-full transition-colors ${
                  done
                    ? "bg-primary text-primary-foreground"
                    : active
                      ? "bg-primary/15 text-primary"
                      : "bg-muted text-muted-foreground"
                }`}
              >
                {done ? (
                  <Check className="h-3.5 w-3.5" />
                ) : active ? (
                  <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-current" />
                )}
              </span>
              <span className={active ? "text-foreground font-medium" : "text-muted-foreground"}>
                {s.title}
              </span>
            </li>
          );
        })}
      </ul>

      <ul className="mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
        {features.map(({ icon: Icon, label }) => (
          <li key={label} className="flex items-center gap-2 text-xs text-muted-foreground">
            <Icon className="h-3.5 w-3.5 text-primary" aria-hidden />
            {label}
          </li>
        ))}
      </ul>
    </div>
  );
}
